/**
 * useTableScrollHeight
 *
 * 为 AntD Table 计算 scroll.y 的像素高度，替代 `calc(100vh - Npx)` 魔数写法。
 *
 * 原理：把 <Table> 包在一个 `flex: 1; min-height: 0; overflow: hidden` 的容器里，
 * 该容器由外层 flex 列布局自动填满剩余空间；本 hook 测量容器可用高度，
 * 减去表头实际高度与分页栏预留空间，得到表体（tbody）可用高度。
 *
 * 相比固定 calc 的优势：
 * - 自动适配不同页面的筛选区字段数量、操作栏高度（无需为每页猜一个偏移量）
 * - 筛选区展开/收起、窗口缩放时通过 ResizeObserver 自动重算
 * - 末行与分页栏始终落在容器可视区内，不会被裁剪
 *
 * 用法：
 *   const { tableWrapRef, scrollY } = useTableScrollHeight();
 *   <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
 *     <Table scroll={{ x: 1600, y: scrollY }} ... />
 *   </div>
 */
import { useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface UseTableScrollHeightOptions {
  /** 表格是否带分页栏。默认 true。false 用于无分页表格（如树表格） */
  hasPagination?: boolean;
  /** 有分页时底部预留高度（px）。默认 72（分页高 32 + 上下 margin 32 + 余量 8） */
  reserved?: number;
  /** 无分页时底部预留间距（px）。默认 8 */
  reservedNoPagination?: number;
  /** 表体最小高度（px），避免容器被隐藏（如未激活的 Tab）时算出负值。默认 200 */
  minHeight?: number;
  /** 表头兜底高度（px），首帧 .ant-table-header 尚未渲染时使用。默认 39 */
  fallbackHeaderHeight?: number;
}

interface UseTableScrollHeightResult {
  /** 挂在包裹 <Table> 的 div 上，该 div 需为 flex:1 / min-height:0 / overflow:hidden */
  tableWrapRef: RefObject<HTMLDivElement>;
  /** 计算得到的 scroll.y 像素值 */
  scrollY: number;
}

export function useTableScrollHeight(
  options: UseTableScrollHeightOptions = {},
): UseTableScrollHeightResult {
  const {
    hasPagination = true,
    reserved = 72,
    reservedNoPagination = 8,
    minHeight = 200,
    fallbackHeaderHeight = 39,
  } = options;

  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState<number>(minHeight);

  useLayoutEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;

    const compute = () => {
      const wrapHeight = el.clientHeight;
      // 容器被隐藏（如未激活 Tab display:none）时高度为 0，保持最小高度，等可见后再重算
      if (wrapHeight <= 0) return;
      const header = el.querySelector<HTMLElement>('.ant-table-header');
      const headerHeight = header?.offsetHeight ?? fallbackHeaderHeight;
      const bottomReserve = hasPagination ? reserved : reservedNoPagination;
      const next = Math.floor(wrapHeight - headerHeight - bottomReserve);
      setScrollY(next > minHeight ? next : minHeight);
    };

    compute();

    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener('resize', compute);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [hasPagination, reserved, reservedNoPagination, minHeight, fallbackHeaderHeight]);

  return { tableWrapRef, scrollY };
}

export default useTableScrollHeight;
